/**
 * Full detail for one outcome — headline, narration sentence, and the
 * supplementary badges (pH, prediction flag, source). Used as the
 * expanded ("More") view inside a StepLog entry; the collapsed view just
 * shows the headline inline, so this only renders once a student asks for
 * the full picture.
 */
import type { ChemistryLabOutcome } from '../types';
import { PH_LABELS, headlineItems } from '../outcomeDisplay';
import { ChemEquation } from './ChemEquation';

interface OutcomePanelProps {
  outcome: ChemistryLabOutcome;
  source?: 'intrinsic' | 'cache' | 'llm';
  /** The burner setting this outcome was computed at — see headlineItems'
   *  own doc for why boiling needs this alongside the outcome. */
  celsius?: number;
}

export function OutcomePanel({ outcome, source, celsius }: OutcomePanelProps): React.JSX.Element {
  const isPredicted = outcome.confidence === 'predicted';
  const headline = headlineItems(outcome, celsius);

  return (
    <div className={`chemlab-outcome${isPredicted ? ' predicted' : ''}`}>
      {outcome.equation && (
        <p className="chemlab-outcome-equation">
          <ChemEquation equation={outcome.equation} />
        </p>
      )}
      {headline.length > 0 && (
        <div className="chemlab-outcome-headline">
          {headline.map((item, i) => (
            <span key={i} className="chemlab-outcome-headline-item">
              {item.swatch
                ? <span className="chemlab-outcome-swatch" style={{ background: item.swatch }} />
                : <span>{item.icon}</span>}
              {item.text}
            </span>
          ))}
        </div>
      )}
      <p className="chemlab-outcome-narration">{outcome.narrationText}</p>
      <div className="chemlab-outcome-badges">
        {outcome.pHBand && <span className="chemlab-badge">{PH_LABELS[outcome.pHBand]}</span>}
        {outcome.boilingPointC !== undefined && (
          <span className="chemlab-badge">boils at {Math.round(outcome.boilingPointC)} °C</span>
        )}
        {isPredicted && <span className="chemlab-badge predicted-flag">⚠ Prediction, not textbook-certain</span>}
        {source && <span className="chemlab-badge">source: {source}</span>}
      </div>
    </div>
  );
}
